import {DialLockMini, MailboxMini} from '@dropbox/dig-illustrations';
import {useMutation} from '@tanstack/react-query';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {snackbarAtom} from 'atoms/snackbar';
import {WorkstreamService} from 'client';
import {EmptyState} from 'components/DSYS/EmptyState';
import {Layout} from 'components/DSYS/Layout';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';

export const WorkstreamAccessRequest = ({
  requested,
  workstreamId,
}: {
  requested: boolean;
  workstreamId: number;
}) => {
  const navigate = useNavigate();

  const {employee} = useAtomValue(loggedInEmployeeAtom);

  const setSnackbar = useSetAtom(snackbarAtom);

  const {mutate: requestAccess, isPending: isRequestingAccess} = useMutation({
    mutationFn: () =>
      getService(WorkstreamService).requestPermissionApiV1WorkstreamAccessPost(workstreamId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['workstream', workstreamId]});
      navigate('.', {replace: true});
    },
  });

  const {mutate: grantRequest, isPending: isGrantingRequest} = useMutation({
    mutationFn: () =>
      getService(WorkstreamService).grantPermissionApiV1WorkstreamGrantPost(
        workstreamId,
        false,
        employee.ldap
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['workstream', workstreamId]});
      navigate('.', {replace: true});
    },
  });

  const handleRequestAccess = async () => {
    requestAccess();
    setSnackbar({text: t('workstream_access_request_cta_request_success')});
  };

  const handleGrantRequest = () => {
    grantRequest();
    setSnackbar({text: t('workstream_access_request_cta_cancel_success')});
  };

  return (
    <Layout.Container>
      <EmptyState
        hideBorder
        image={
          !requested ? (
            <DialLockMini width={64} altText="Lock" />
          ) : (
            <MailboxMini width={64} altText="Mailbox" />
          )
        }
        title={
          !requested
            ? t('workstream_access_request_title')
            : t('workstream_access_request_title_requested')
        }
        body={
          !requested
            ? t('workstream_access_request_body')
            : t('workstream_access_request_body_requested')
        }
        cta={
          !requested
            ? {
                text: t('workstream_access_request_cta_request'),
                onClick: handleRequestAccess,
                loading: isRequestingAccess,
              }
            : {
                text: t('workstream_access_request_cta_cancel'),
                onClick: handleGrantRequest,
                loading: isGrantingRequest,
              }
        }
      />
    </Layout.Container>
  );
};
